import { Link, useNavigate } from 'react-router-dom';
import DeckMenu from './DeckMenu.tsx';
import { DECK_DRAG_MIME } from '../../components/CategorySidebar.tsx';
import { relativeTime, colourHex, formatBuiltBy, formatBuiltByShort } from '../../lib/format.ts';
import { swatchVar } from '../../../shared/palette.ts';
import { Refresh } from '../../lib/icons.tsx';
import type { Dispatch, MouseEvent, SetStateAction } from 'react';
import type { CoverWarm } from '../../lib/coverWarm.ts';
import type { DeckSummary } from '../../../shared/deck.ts';
import type { CategoryColour, CategoryEntry } from '../../../shared/api.ts';
import type { DeckSort } from './deckFilterState.ts';
import type { DeckCardActions } from './DeckMenu.tsx';

/**
 * Which column headers sort, and what each one means.
 *
 * `DeckSort` has no direction axis — each sort is one fixed order (see
 * `DECK_SORT_COMPARATORS`) — so a header is a switch, not a toggle, and its
 * caret points the way that sort actually runs rather than offering a reverse
 * the state object cannot express. CATEGORY, STATUS and MODEL have no matching
 * `DeckSort` value, so they are plain labels: a header that looks pressable and
 * does nothing is worse than one that doesn't.
 *
 * `opened` is the fourth sort and has no column of its own (there is no
 * "Last opened" in the design), so it is reachable from the Sort control only.
 */
const SORTABLE = {
  name: { sort: 'name', dir: 'ascending' },
  slides: { sort: 'slides', dir: 'descending' },
  updated: { sort: 'edited', dir: 'descending' },
} as const satisfies Record<string, { sort: DeckSort; dir: 'ascending' | 'descending' }>;

type DeckTableProps = {
  /** The decks to show, already filtered and sorted by the page. */
  decks: DeckSummary[];
  /** The category registry, for the colour dot beside a deck's category. Bare
   *  paths or `{ path, colour }` — an install written before colours existed is
   *  still a plain `string[]`. */
  categories: readonly (string | CategoryEntry)[];
  /** Cached cover URLs by slug, as the dashboard holds them. */
  covers: Record<string, string | null>;
  /** The page's warm queue — the same placeholder/retry/observer mechanism the
   *  grid cards use, at 34×22 instead of 16:9. */
  warm: CoverWarm;
  busy: boolean;
  /** Slugs ticked anywhere in the page's selection. */
  selected: Set<string>;
  /** The page's single open-menu slot, and the deck being dragged. */
  openMenuKey: string | null;
  draggingSlug: string | null;
  /** The active sort, so the right header carries the caret. */
  sort: DeckSort;
  onSort: (sort: DeckSort) => void;
  onToggleSelected: (slug: string, shiftKey: boolean) => void;
  setMenuKey: Dispatch<SetStateAction<string | null>>;
  setDraggingSlug: Dispatch<SetStateAction<string | null>>;
  actions: DeckCardActions;
};

/** Registry path (case-insensitively) → its tint, so a row's dot matches the
 *  sidebar's. Built per render: the registry is a couple of dozen entries. */
function colourMap(categories: readonly (string | CategoryEntry)[]): Map<string, CategoryColour> {
  const map = new Map<string, CategoryColour>();
  for (const entry of categories || []) {
    if (typeof entry === 'string' || !entry?.path) continue;
    if (entry.colour) map.set(entry.path.toLowerCase(), entry.colour);
  }
  return map;
}

/** A click that should open the deck — anything except the row's own controls.
 *  The name cell holds a real link (that is what gives the row a URL to copy,
 *  middle-click and open in a tab); the rest of the row is a convenience on top
 *  of it, and must not fire when the checkbox, the kebab or the retry button
 *  was the thing pressed. */
function isRowBackground(e: MouseEvent<HTMLTableRowElement>): boolean {
  const el = e.target as HTMLElement | null;
  return !el?.closest('a, button, input, label, [role="menu"]');
}

/**
 * The `list` view: a dense data table, one row per deck.
 *
 * A real `<table>` rather than a grid of divs, because this genuinely is
 * tabular data — eight aligned facts about each deck — and the markup is what
 * gives a screen reader the column headings, the row/column count and the sort
 * state for free.
 *
 * Every affordance the grid card carries survives here: the select checkbox
 * (shift-click extends, and once anything is ticked every box shows), the drag
 * source that files a deck into a category, the broken badge, and the kebab
 * menu. A view switch that quietly costs the user a verb is the main regression
 * risk in this run.
 */
export default function DeckTable({
  decks,
  categories,
  covers,
  warm,
  busy,
  selected,
  openMenuKey,
  draggingSlug,
  sort,
  onSort,
  onToggleSelected,
  setMenuKey,
  setDraggingSlug,
  actions,
}: DeckTableProps) {
  const navigate = useNavigate();
  const { warmingNow, coverStalled, retryCover, coverRef } = warm;
  const colours = colourMap(categories);
  const isSelecting = selected.size > 0;

  /** One sortable column heading. The caret only ever appears on the active
   *  one — a resting header row of eight carets says nothing. */
  const sortHead = (key: keyof typeof SORTABLE, label: string, className?: string) => {
    const { sort: value, dir } = SORTABLE[key];
    const active = sort === value;
    return (
      <th scope="col" className={className} aria-sort={active ? dir : 'none'}>
        <button type="button" className="deck-table__sort" onClick={() => onSort(value)}>
          {label}
          <span className={'deck-table__caret' + (active ? ' is-active' : '')} aria-hidden="true">
            {dir === 'ascending' ? '▲' : '▼'}
          </span>
        </button>
      </th>
    );
  };

  return (
    <table className={'deck-table' + (isSelecting ? ' is-selecting' : '')}>
      <thead>
        <tr>
          {/* Select and cover are two columns, not one. Laying the checkbox
              over the thumbnail hid the cover of the very row you were about
              to act on, which is the one row you want to recognise. */}
          <th scope="col" className="deck-table__col-select">
            <span className="deck-table__sr">Select</span>
          </th>
          <th scope="col" className="deck-table__col-thumb">
            <span className="deck-table__sr">Cover</span>
          </th>
          {sortHead('name', 'Name')}
          <th scope="col" className="deck-table__col-category">
            Category
          </th>
          <th scope="col" className="deck-table__col-status">
            Status
          </th>
          <th scope="col" className="deck-table__col-model">
            Model
          </th>
          {sortHead('slides', 'Slides', 'deck-table__col-slides')}
          {sortHead('updated', 'Updated', 'deck-table__col-updated')}
          <th scope="col" className="deck-table__col-actions">
            <span className="deck-table__sr">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {decks.map((d) => {
          const cover = covers[d.slug] || null;
          // Same rule as the card: a broken or empty deck has no first slide,
          // so it is never given a slot or queued for one.
          const wantsCover = !d.broken && d.slideCount > 0;
          const stalled = wantsCover && !cover && coverStalled(d.slug);
          const category = d.category || null;
          const colour = category ? colours.get(category.toLowerCase()) : undefined;
          const model = formatBuiltByShort(d.builtBy);
          return (
            <tr
              key={d.slug}
              className={
                'deck-table__row' +
                (selected.has(d.slug) ? ' is-selected' : '') +
                (draggingSlug === d.slug ? ' is-dragging' : '') +
                (openMenuKey === d.slug ? ' is-menu-open' : '')
              }
              // Same drag contract as the grid card: the payload the category
              // rail reads, plus a text/plain fallback so dropping anywhere
              // else pastes something sane.
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(DECK_DRAG_MIME, d.slug);
                e.dataTransfer.setData('text/plain', d.title);
                e.dataTransfer.effectAllowed = 'copy';
                setDraggingSlug(d.slug);
                setMenuKey(null);
              }}
              onDragEnd={() => setDraggingSlug(null)}
              onClick={(e) => {
                if (isRowBackground(e)) navigate(`/editor/${d.slug}`);
              }}
            >
              {/* Its own leading cell. It is a real checkbox at all times, so
                  keyboard and screen readers always have it; it only becomes
                  VISIBLE on hover, on focus, or once anything at all is
                  ticked, so a resting table reads as a list of decks rather
                  than a form. Hidden by opacity, never by `display`, so it
                  never leaves the accessibility tree and the column never
                  changes width under you. */}
              <td className="deck-table__col-select">
                <label className="deck-table__select" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(d.slug)}
                    // React's onChange carries no shiftKey, so the range
                    // lives on onClick and the box is driven by `checked`.
                    onClick={(e) => onToggleSelected(d.slug, e.shiftKey)}
                    onChange={() => {}}
                    aria-label={`Select ${d.title}`}
                  />
                </label>
              </td>
              <td className="deck-table__col-thumb">
                <div className="deck-table__thumbwrap">
                  {/* 34×22, reserved before any image lands so a cover
                      arriving mid-session never reflows the table. `ref`
                      registers the row with the warm queue's observer:
                      scrolling to it IS the request for its cover. */}
                  <span
                    className="deck-table__thumb"
                    aria-hidden="true"
                    ref={wantsCover && !cover ? coverRef(d.slug) : undefined}
                  >
                    <span className="deck-table__stripe">
                      {(d.sections || [])
                        .filter((s) => s.n !== null)
                        .map((s) => (
                          <span key={s.key} className="deck-table__band" style={{ background: colourHex(s.color) }} />
                        ))}
                    </span>
                    {cover ? (
                      // draggable={false}: the ROW is the drag source, and a
                      // native image drag would hand the rail a payload it
                      // cannot read.
                      <img
                        className="deck-table__thumb-img"
                        src={cover}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                      />
                    ) : (
                      // Shimmer only while something is actually coming.
                      <span
                        className={'deck-table__thumb-ph' + (wantsCover && warmingNow(d.slug) ? ' is-working' : '')}
                      />
                    )}
                  </span>
                  {/* Given up on for this session. The tile is the way back,
                      exactly as on the card — a stalled cover must never be a
                      dead grey box with nothing to press. */}
                  {stalled && (
                    <button
                      type="button"
                      className="deck-table__retry"
                      title="This cover didn't render. Try again."
                      aria-label={`Retry the cover for ${d.title || d.slug}`}
                      onClick={() => retryCover(d.slug)}
                    >
                      <Refresh size={12} />
                    </button>
                  )}
                </div>
              </td>
              <td>
                <Link to={`/editor/${d.slug}`} className="deck-table__name" draggable={false} title={d.title}>
                  <span className="deck-table__title">
                    {d.title}
                    {d.broken && <span className="badge badge--warn">Broken</span>}
                  </span>
                  <span className="deck-table__slug">{d.slug}</span>
                </Link>
              </td>
              <td className="deck-table__col-category">
                {category ? (
                  <span className="deck-table__cat" title={`Category: ${category}`}>
                    <span
                      className="deck-table__dot"
                      style={colour ? { background: swatchVar(colour) } : undefined}
                      aria-hidden="true"
                    />
                    {/* The name gets its own box because the row is a flex
                        container, and `text-overflow` never reaches a bare
                        text node inside one — it would hard-clip mid-letter
                        with no ellipsis. */}
                    <span className="deck-table__cat-name">{category.replace('/', ' › ')}</span>
                  </span>
                ) : (
                  <span className="deck-table__none">Uncategorised</span>
                )}
              </td>
              <td className="deck-table__col-status">
                <span className={'deck-table__pill' + (d.publishedUrl ? ' is-published' : '')}>
                  <span className="deck-table__pill-dot" aria-hidden="true" />
                  {d.publishedUrl ? 'Published' : 'Draft'}
                </span>
              </td>
              <td className="deck-table__col-model">
                {/* The full stamp (provider, effort) stays in the tooltip and
                    the deck info modal, as on the card. */}
                <span
                  className="deck-table__model"
                  title={d.builtBy ? formatBuiltBy(d.builtBy) || undefined : undefined}
                >
                  {model || '–'}
                </span>
              </td>
              <td className="deck-table__col-slides">{d.slideCount || '–'}</td>
              <td className="deck-table__col-updated">{relativeTime(d.lastEditedAt)}</td>
              <td className="deck-table__col-actions">
                <DeckMenu
                  deck={d}
                  cardKey={d.slug}
                  menuOpen={openMenuKey === d.slug}
                  busy={busy}
                  setMenuKey={setMenuKey}
                  actions={actions}
                  className="deck-table__menu iconbtn"
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
